/**
 * AdService - Manages advertisements (FREE version only)
 * 
 * Handles AdMob integration for banner, interstitial, and rewarded ads.
 * This service is completely stripped out in PAID versions.
 */

import { configService } from './ConfigService';

class AdService {
  constructor() {
    this.initialized = false;
    this.config = null;
    this.lessonCount = 0;
    this.challengeCount = 0;
  }

  /**
   * Initialize AdMob SDK
   */
  async initialize() {
    this.config = configService.getConfig();

    // Only initialize if ads are enabled
    if (!this.isAdsEnabled()) {
      console.log('Ads disabled (PAID version or not configured)');
      return false;
    }

    // Check if AdMob plugin is available
    if (!window.AdMob) {
      console.warn('AdMob plugin not available');
      return false;
    }

    try {
      const adMobConfig = {
        appId: this.config.monetization.adMobAppId,
        isTesting: this.config.monetization.adConfig?.testMode || false
      };

      await window.AdMob.initialize(adMobConfig);
      this.initialized = true;
      console.log('AdMob initialized successfully');
      return true;
    } catch (error) {
      console.error('AdMob initialization failed:', error);
      return false;
    }
  }

  /**
   * Check if ads are enabled
   */
  isAdsEnabled() {
    return this.config?.monetization?.enabled === true &&
           this.config.monetization.type === 'ads' &&
           this.config.monetization.adMobAppId;
  }

  /**
   * Check if banner should be shown on a specific location
   */
  shouldShowBanner(location) {
    if (!this.isAdsEnabled()) return false;

    const adConfig = this.config.monetization.adConfig;
    if (!adConfig) return false;

    switch (location) {
      case 'home':
        return adConfig.showBannerOnHome !== false;
      case 'lessons':
        return adConfig.showBannerOnLessons !== false;
      case 'challenges':
        return adConfig.showBannerOnChallenges === true;
      default:
        return false;
    }
  }

  /**
   * Show banner ad
   */
  async showBanner(position = 'bottom') {
    if (!this.isAdsEnabled() || !window.AdMob || !this.initialized) {
      return false;
    }

    try {
      const bannerOptions = {
        adId: this.config.monetization.adUnits.banner,
        position: position === 'top' 
          ? window.AdMob.AD_POSITION?.TOP_CENTER || 1
          : window.AdMob.AD_POSITION?.BOTTOM_CENTER || 8,
        isTesting: this.config.monetization.adConfig?.testMode || false,
        autoShow: true
      };

      await window.AdMob.createBanner(bannerOptions);
      console.log('Banner ad displayed');
      return true;
    } catch (error) {
      console.error('Banner ad error:', error);
      return false;
    }
  }

  /**
   * Hide banner ad
   */
  async hideBanner() {
    if (!window.AdMob) return;

    try {
      await window.AdMob.hideBanner();
    } catch (error) {
      console.error('Hide banner error:', error);
    }
  }

  /**
   * Remove banner ad completely
   */
  async removeBanner() {
    if (!window.AdMob) return;

    try {
      await window.AdMob.removeBanner();
    } catch (error) {
      console.error('Remove banner error:', error);
    }
  }

  /**
   * Show interstitial ad after lesson (based on frequency)
   */
  async showAfterLesson() {
    if (!this.isAdsEnabled()) return false;

    const adConfig = this.config.monetization.adConfig;
    if (!adConfig?.interstitialAfterLesson) return false;

    this.lessonCount++;
    const frequency = adConfig.interstitialFrequency || 3;

    if (this.lessonCount >= frequency) {
      this.lessonCount = 0;
      return await this.showInterstitial();
    }

    return false;
  }

  /**
   * Show interstitial ad after challenge (based on frequency)
   */
  async showAfterChallenge() {
    if (!this.isAdsEnabled()) return false;

    const adConfig = this.config.monetization.adConfig;
    if (!adConfig?.interstitialAfterChallenge) return false;

    this.challengeCount++;
    const frequency = adConfig.interstitialFrequency || 3;

    if (this.challengeCount >= frequency) {
      this.challengeCount = 0;
      return await this.showInterstitial();
    }

    return false;
  }

  /**
   * Show interstitial ad
   */
  async showInterstitial() {
    if (!this.isAdsEnabled() || !window.AdMob || !this.initialized) {
      return false;
    }

    try {
      const interstitialOptions = {
        adId: this.config.monetization.adUnits.interstitial,
        isTesting: this.config.monetization.adConfig?.testMode || false
      };

      await window.AdMob.prepareInterstitial(interstitialOptions);
      await window.AdMob.showInterstitial();
      console.log('Interstitial ad displayed');
      return true;
    } catch (error) {
      console.error('Interstitial ad error:', error);
      return false;
    }
  }

  /**
   * Show rewarded ad and call callback when reward is granted
   */
  async showRewarded(onRewarded, onClosed = null) {
    // In PAID version, give reward immediately
    if (!this.isAdsEnabled()) {
      if (onRewarded) onRewarded();
      return true;
    }

    if (!window.AdMob || !this.initialized) {
      // Give reward anyway if ad system fails
      if (onRewarded) onRewarded();
      return false;
    }

    try {
      const rewardedOptions = {
        adId: this.config.monetization.adUnits.rewarded,
        isTesting: this.config.monetization.adConfig?.testMode || false
      };

      // Set up reward listener
      const rewardListener = () => {
        if (onRewarded) onRewarded();
        window.AdMob.off('onRewardVideoAdRewarded', rewardListener);
      };

      const closeListener = () => {
        if (onClosed) onClosed();
        window.AdMob.off('onRewardVideoAdClosed', closeListener);
      };

      window.AdMob.on('onRewardVideoAdRewarded', rewardListener);
      window.AdMob.on('onRewardVideoAdClosed', closeListener);

      await window.AdMob.prepareRewardVideoAd(rewardedOptions);
      await window.AdMob.showRewardVideoAd();
      console.log('Rewarded ad displayed');
      return true;
    } catch (error) {
      console.error('Rewarded ad error:', error);
      // Give reward anyway if ad fails
      if (onRewarded) onRewarded();
      return false;
    }
  }

  /**
   * Check if rewarded ads are enabled for hints
   */
  isRewardedForHintsEnabled() {
    if (!this.isAdsEnabled()) return false;
    return this.config.monetization.adConfig?.rewardedForHints === true;
  }

  /**
   * Get upgrade prompt configuration
   */
  getUpgradePromptConfig() {
    if (!this.config?.monetization) return null;

    return {
      showPrompt: this.config.monetization.showUpgradePrompt === true,
      url: this.config.monetization.upgradeUrl,
      price: this.config.monetization.upgradePrice,
      features: this.config.monetization.upgradeFeatures || []
    };
  }

  /**
   * Open upgrade URL in browser
   */
  openUpgradeUrl() {
    const upgradeUrl = this.config?.monetization?.upgradeUrl;
    if (upgradeUrl) {
      window.open(upgradeUrl, '_system');
    }
  }

  /**
   * Reset ad counters
   */
  resetCounters() {
    this.lessonCount = 0;
    this.challengeCount = 0;
  }

  /**
   * Check if service is ready
   */
  isReady() {
    return this.initialized || !this.isAdsEnabled();
  }
}

// Export singleton instance
export const adService = new AdService();
export default adService;